import { useEffect, useState } from "react";
import axios from "axios";
import { Camera, Crosshair, Footprints, History, Lightbulb, Loader2, Mail, RotateCcw, Sparkles, Check, AlertTriangle, LifeBuoy } from "lucide-react";
import { jsPDF } from "jspdf";
import { fmt } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const STEP_KEY = { 1: "company", 2: "materials", 3: "plots", 4: "documents", 5: "dds" };
const STEP_OF = (k) => (k.startsWith("company") ? 1 : ["materials_undecided", "material_country", "material_harvest", "material_species"].includes(k) ? 2 : ["no_plots", "material_no_plot"].includes(k) ? 3 : k === "no_documents" ? 4 : 6);
const btn = "inline-flex h-11 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold transition-colors";

export function PrefillOffer({ t, pf, onChoose }) {
  const [busy, setBusy] = useState(false);
  const go = async (c) => { setBusy(true); await onChoose(c); setBusy(false); };
  return (
    <div className="mt-5 overflow-hidden rounded-2xl border border-[#D52B3B]/30 bg-white shadow-sm" data-testid="portal-prefill-offer">
      <div className="flex items-start gap-3 bg-gradient-to-r from-[#D52B3B]/10 to-transparent p-5">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#D52B3B]" />
        <div><p className="font-semibold text-slate-900">{t.prefill_title}</p><p className="mt-1 text-sm text-slate-600">{fmt(t.prefill_text, { v: pf.version })}</p></div>
      </div>
      <div className="flex flex-col gap-2 p-4 sm:flex-row">
        <button disabled={busy} data-testid="portal-prefill-last" onClick={() => go("last")} className={cn(btn, "bg-[#D52B3B] text-white hover:bg-[#b82231]")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <History className="h-4 w-4" />}{t.prefill_last}</button>
        <button disabled={busy} data-testid="portal-prefill-fresh" onClick={() => go("fresh")} className={cn(btn, "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50")}>{t.prefill_fresh}</button>
      </div>
    </div>
  );
}

export function ConfirmBar({ t, pf, step, onConfirm }) {
  const key = STEP_KEY[step];
  if (pf?.status !== "accepted" || !key) return null;
  const ok = pf.steps?.[key];
  return (
    <div className={cn("mb-5 flex flex-wrap items-center gap-3 rounded-xl border px-4 py-3 text-sm", ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-900")} data-testid={`portal-confirm-bar-${key}`}>
      {ok ? <Check className="h-4 w-4" /> : <History className="h-4 w-4" />}
      <span className="flex-1 font-medium">{ok ? t.step_confirmed : t.from_last_year}</span>
      {!ok && <button data-testid={`portal-confirm-${key}`} onClick={() => onConfirm(key)} className={cn(btn, "h-9 bg-[#1C1C1E] text-white hover:bg-black")}>{t.confirm_step}</button>}
      {key === "documents" && pf.expired?.length > 0 && (
        <div className="w-full rounded-lg bg-white/70 p-2 text-rose-700" data-testid="portal-expired-docs">
          <p className="font-semibold">{t.expired_title}</p>
          {pf.expired.map((d) => <p key={d.filename} className="text-xs">{t[`dt_${d.doc_type}`] || d.doc_type} · {d.filename} · {d.expiry}</p>)}
        </div>)}
    </div>
  );
}

export function Progress({ t, missing, saving, savedAt }) {
  const steps = new Set(missing.map((m) => (m.key === "unconfirmed_step" ? { company: 1, materials: 2, plots: 3, documents: 4, dds: 5 }[m.step] : STEP_OF(m.key))));
  const pct = Math.round(((6 - steps.size) / 6) * 100);
  const mins = Math.max(1, Math.round(missing.length * 1.5));
  return (
    <div className="mt-5" data-testid="portal-progress">
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span data-testid="portal-progress-pct">{fmt(t.progress, { p: pct })}</span>
        <span className="flex items-center gap-2"><span data-testid="portal-minutes-left">{missing.length ? fmt(t.minutes_left, { n: mins }) : t.done_soon}</span>
          <span className={cn("rounded-full px-2 py-0.5 font-semibold transition-opacity", saving ? "bg-slate-100 text-slate-500" : savedAt ? "bg-emerald-50 text-emerald-700" : "opacity-0")} data-testid="portal-saved-tick">{saving ? t.saving : t.saved_tick}</span></span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-[#D52B3B] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function HelpTip({ t, step, helpEmail }) {
  return (
    <div className="mt-6 flex flex-wrap items-start gap-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600" data-testid="portal-help-tip">
      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /><p className="flex-1">{t.help_tips[step - 1]}</p>
      {helpEmail && <a href={`mailto:${helpEmail}`} data-testid="portal-need-help" className="inline-flex items-center gap-1 font-semibold text-[#D52B3B] hover:underline"><LifeBuoy className="h-4 w-4" />{t.need_help}</a>}
    </div>
  );
}

export function EmailLink({ t, base }) {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const x = (await axios.post(`${base}/email-link`)).data; setR(x.ok ? fmt(t.email_sent, { email: x.to }) : fmt(t.email_failed, { reason: x.error })); }
    catch (e) { setR(fmt(t.email_failed, { reason: e?.response?.data?.detail || "" })); } finally { setBusy(false); }
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <button data-testid="portal-email-link" disabled={busy} onClick={send} className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D52B3B] hover:underline">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}{t.email_link}</button>
      {r && <span className="text-xs text-slate-600" data-testid="portal-email-result">{r}</span>}
    </span>
  );
}

export function Changes({ t, base }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${base}/changes`).then((r) => setD(r.data)).catch(() => setD(null)); }, [base]);
  if (!d?.base_version) return null;
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid="portal-changes">
      <p className="mb-2 font-semibold text-slate-900">{t.changes_title}</p>
      {!d.changes.length ? <p className="text-sm text-slate-500">{t.no_changes}</p> : (
        <ul className="space-y-1 text-sm">{d.changes.map((c, i) => <li key={i} className="flex flex-wrap gap-1" data-testid={`portal-change-${i}`}><span className="rounded bg-slate-100 px-1.5 text-xs font-semibold">{t.ch_area[c.area]}</span><b>{c.item}</b>: <span className="text-slate-400 line-through">{String(c.before ?? "—")}</span> → <span className="text-slate-800">{String(c.after ?? "—")}</span></li>)}</ul>)}
    </div>
  );
}

const compress = (file) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    res({ url: c.toDataURL("image/jpeg", 0.7), w: c.width, h: c.height });
  };
  img.onerror = rej;
  img.src = URL.createObjectURL(file);
});

export function PhotoCapture({ t, onPdf, busy }) {
  const [photos, setPhotos] = useState([]);
  const [making, setMaking] = useState(false);
  const add = async (files, replace) => {
    const ps = await Promise.all([...files].map(compress));
    setPhotos((cur) => (replace !== undefined ? cur.map((p, i) => (i === replace ? ps[0] : p)) : [...cur, ...ps]));
  };
  const make = async () => {
    setMaking(true);
    const pdf = new jsPDF({ unit: "px", format: [photos[0].w, photos[0].h], orientation: photos[0].w > photos[0].h ? "l" : "p" });
    photos.forEach((p, i) => { if (i) pdf.addPage([p.w, p.h], p.w > p.h ? "l" : "p"); pdf.addImage(p.url, "JPEG", 0, 0, p.w, p.h); });
    const file = new File([pdf.output("blob")], `photos-${Date.now()}.pdf`, { type: "application/pdf" });
    await onPdf(file);
    setPhotos([]); setMaking(false);
  };
  const Pick = ({ label, replace, testid }) => (
    <label className={cn(btn, "h-10 cursor-pointer border border-slate-300 bg-white text-slate-700 hover:bg-slate-50")}>
      {replace !== undefined ? <RotateCcw className="h-4 w-4" /> : <Camera className="h-4 w-4" />}{label}
      <input type="file" accept="image/*" capture="environment" className="hidden" data-testid={testid} multiple={replace === undefined} onChange={(e) => { const f = e.target.files; if (f?.length) add(f, replace); e.target.value = ""; }} />
    </label>);
  return (
    <div className="rounded-xl border border-dashed border-slate-300 p-4" data-testid="portal-photo-capture">
      <div className="flex flex-wrap gap-2"><Pick label={photos.length ? t.add_photo : t.take_photo} testid="portal-photo-input" /></div>
      {photos.length > 0 && (
        <>
          <p className="mt-3 text-xs text-slate-500" data-testid="portal-photo-count">{fmt(t.photos_n, { n: photos.length })}</p>
          <div className="mt-2 flex flex-wrap gap-3">{photos.map((p, i) => (
            <div key={i} className="w-28 space-y-1" data-testid={`portal-photo-preview-${i}`}><img src={p.url} alt="" className="h-28 w-28 rounded-lg object-cover ring-1 ring-slate-200" /><Pick label={t.retake} replace={i} testid={`portal-photo-retake-${i}`} /></div>))}</div>
          <button data-testid="portal-photo-pdf" disabled={making || busy} onClick={make} className={cn(btn, "mt-3 bg-[#D52B3B] text-white hover:bg-[#b82231]")}>{making || busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{making ? t.preparing : t.make_pdf}</button>
        </>)}
    </div>
  );
}

const areaHa = (pts) => {
  if (pts.length < 3) return 0;
  const lat0 = (pts.reduce((a, p) => a + p[1], 0) / pts.length) * (Math.PI / 180);
  const xy = pts.map(([lo, la]) => [lo * 111320 * Math.cos(lat0), la * 110540]);
  const a = xy.reduce((s, [x, y], i) => { const [x2, y2] = xy[(i + 1) % xy.length]; return s + x * y2 - x2 * y; }, 0);
  return Math.abs(a / 2) / 10000;
};

const locate = () => new Promise((res, rej) => {
  if (!navigator.geolocation) return rej(new Error("unavailable"));
  return navigator.geolocation.getCurrentPosition((p) => res(p.coords), rej, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
});

export function LocationTools({ t, onPoint, onPolygon }) {
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [walk, setWalk] = useState(null);
  const here = async () => {
    setBusy(true); setMsg(null);
    try {
      const c = await locate();
      const n = Math.round(c.accuracy);
      onPoint([c.longitude, c.latitude]);
      setMsg(n > 50 ? { warn: true, text: fmt(t.loc_approx, { n }) } : { text: fmt(t.loc_accuracy, { n }) });
      return c;
    } catch { setMsg({ err: true, text: t.loc_denied }); return null; } finally { setBusy(false); }
  };
  const corner = async () => {
    setBusy(true); setMsg(null);
    try { const c = await locate(); setWalk((w) => [...w, [c.longitude, c.latitude]]); if (c.accuracy > 50) setMsg({ warn: true, text: fmt(t.loc_approx, { n: Math.round(c.accuracy) }) }); }
    catch { setMsg({ err: true, text: t.loc_denied }); } finally { setBusy(false); }
  };
  const ha = walk ? areaHa(walk) : 0;
  return (
    <div className="space-y-3 rounded-xl border border-slate-200 p-4" data-testid="portal-location-tools">
      <div className="flex flex-wrap gap-2">
        <button data-testid="portal-use-location" disabled={busy} onClick={here} className={cn(btn, "h-10 bg-[#1C1C1E] text-white hover:bg-black")}>{busy && !walk ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}{busy && !walk ? t.locating : t.use_location}</button>
        {!walk && <button data-testid="portal-walk-start" onClick={() => { setWalk([]); setMsg(null); }} className={cn(btn, "h-10 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50")}><Footprints className="h-4 w-4" />{t.walk}</button>}
      </div>
      {msg && <p className={cn("flex items-center gap-2 text-sm", msg.err ? "text-rose-700" : msg.warn ? "text-amber-700" : "text-emerald-700")} data-testid={msg.err ? "portal-location-denied" : msg.warn ? "portal-location-approx" : "portal-location-ok"}>{(msg.err || msg.warn) && <AlertTriangle className="h-4 w-4" />}{msg.text}</p>}
      {walk && (
        <div className="space-y-2 rounded-lg bg-slate-50 p-3" data-testid="portal-walk-panel">
          <p className="text-sm text-slate-600">{t.walk_intro}</p>
          <p className="text-sm font-semibold"><span data-testid="portal-walk-corners">{fmt(t.corners_n, { n: walk.length })}</span> · <span data-testid="portal-walk-area">{fmt(t.live_area, { ha: ha.toFixed(2) })}</span></p>
          <div className="flex flex-wrap gap-2">
            <button data-testid="portal-walk-add" disabled={busy} onClick={corner} className={cn(btn, "h-10 bg-[#D52B3B] text-white hover:bg-[#b82231]")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}{t.add_corner}</button>
            {walk.length > 0 && <button data-testid="portal-walk-undo" onClick={() => setWalk((w) => w.slice(0, -1))} className={cn(btn, "h-10 border border-slate-300 bg-white text-slate-700")}>{t.undo_corner}</button>}
            {walk.length >= 3 && <button data-testid="portal-walk-finish" onClick={() => { onPolygon([...walk, walk[0]]); setWalk(null); }} className={cn(btn, "h-10 bg-[#1C1C1E] text-white")}>{t.finish_polygon}</button>}
            <button data-testid="portal-walk-cancel" onClick={() => setWalk(null)} className={cn(btn, "h-10 text-slate-500")}>{t.cancel}</button>
          </div>
        </div>)}
    </div>
  );
}
